import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from "reactstrap";
import { Icon } from "@cadmakers-ui/icons";
import { PrimaryButton } from "@cadmakers-ui/core";
import styles from "../../../Styles/RelationshipFeedbackDisplayModal.module.scss";
import { FeedbackAutocompleteItem } from "../../../../Layout/Models/AutocompleteItem";
import { Feedback, RelationshipDisplayFeedback } from "../../../Models/Feedback";
import { ParentFeedbackDropdown } from "../FeedbackDropdowns/ParentFeedbackDropdown";
import { endpoints } from "../../../Constants/endpoints";
import { ApiRequest, useShowSnack } from "../../../../Common";
import ResponseHandler from "../../../../App/ResponseHandler";
import { ResponseResult } from "../../../../Common/Models/ResponseResult";
import { ChildFeedbackDisplayList } from "./ChildFeedbackDisplayList";

export type Props = {
  feedback: Feedback;
  parent?: RelationshipDisplayFeedback;
  childrenList?: RelationshipDisplayFeedback[];
  options: RelationshipDisplayFeedback[];
  isOpen: boolean;
  toggleModal: () => void;
  onRelationshipChange?: () => void;
  onRelationshipSelect: (value: number, label: string) => void;
  onRelationshipDeselect: (value: number, label: string) => void;
};

export const RelationshipFeedbackDisplayModal = (props: Props) => {
  const showSnack = useShowSnack();
  const defaultError = {
    hasError: false,
    message: ""
  };

  const [displayParent, setDisplayParent] = useState(
    props.feedback.parentFeedback?.isActive ? props.parent : undefined
  );
  const [displayChild, setDisplayChild] = useState({ value: -1, label: "Select" });
  const [childItemsList, setChildItemsList] = useState(props.childrenList);
  const [parentError, setParentError] = useState(defaultError);
  const [childError, setChildError] = useState(defaultError);
  const [generalError, setGeneralError] = useState(defaultError);
  const originalChildItemsList = props.feedback.children;

  // Check that the selected feedback is not the current feedback
  const selectedIsCurrent = (feedbackId?: number) => {
    if (feedbackId) {
      return props.feedback.id === feedbackId;
    }
    return false;
  };

  // Check that selected children are not the current feedback
  const childrenAreCurrent = () => {
    if (childItemsList) {
      for (let i = 0; i < childItemsList.length; i++) {
        if (selectedIsCurrent(childItemsList[i].value)) {
          return true;
        }
      }
    }
    return false;
  };

  // Check if the selected parent is in the list of selected children
  const isParentInChildItems = () => {
    if (!displayParent || !childItemsList) {
      return false;
    }

    for (let i = 0; i < childItemsList.length; i++) {
      if (childItemsList[i].value === displayParent?.value) {
        return true;
      }
    }
    return false;
  };

  // Save relationships
  const onSave = () => {
    setParentError(defaultError);
    setChildError(defaultError);
    setGeneralError(defaultError);
    if (childItemsList || displayParent) {
      // Check if any inputs are the current feedback
      if (selectedIsCurrent(displayParent?.value) || childrenAreCurrent()) {
        if (selectedIsCurrent(displayParent?.value)) {
          setParentError({
            hasError: true,
            message: "Current feedback cannot be assigned as its own parent"
          });
        }

        if (childrenAreCurrent()) {
          setChildError({
            hasError: true,
            message: "Current feedback cannot be assigned as its own child"
          });
          setDisplayChild({ value: -1, label: "Select" });
          setChildItemsList(
            childItemsList?.filter(c => {
              return !selectedIsCurrent(c.value);
            })
          );
        }
        return;
      }

      // Check if parent and child inputs are the same
      if (isParentInChildItems()) {
        setParentError({
          hasError: true,
          message: "Parent and child feedback cannot be the same feedback"
        });

        setChildError({
          hasError: true,
          message: "Parent and child feedback cannot be the same feedback"
        });
        return;
      }
      setParentError(defaultError);
      setChildError(defaultError);

      const url: string = endpoints.feedback.feedbackRelationships(props.feedback.id);
      const childrenIds = childItemsList?.map(child => {
        return child.value;
      });
      const originalIds = originalChildItemsList?.map(child => {
        return child.id;
      });

      const addChildrenIds = childrenIds?.filter(child => !originalIds?.includes(child));
      const deleteChildrenIds = originalIds?.filter(child => !childrenIds?.includes(child));
      let parentId = displayParent?.value;
      if (parentId === -1) {
        parentId = undefined;
      }

      ApiRequest.fromPath(url)
        .withJson({
          parentId,
          addChildrenIds,
          deleteChildrenIds
        })
        .withAuthHeader()
        .put()
        .then((response: Response) => {
          ResponseHandler.handle(
            response,
            (result: ResponseResult) => {
              setParentError(defaultError);
              setChildError(defaultError);
              setGeneralError(defaultError);
              if (props.onRelationshipChange) {
                props.onRelationshipChange();
                props.toggleModal();
              }
            },
            (result: ResponseResult) => {
              setGeneralError({ hasError: true, message: result.error.message });
              setChildItemsList(
                childItemsList?.filter(c => {
                  return c.value !== displayChild.value;
                })
              );
            }
          );
        });
    }
  };

  // Set display parent to selected parent
  const onSelectParent = (
    parentId: number,
    parentTitle: string,
    prevParentId?: number,
    prevParentTitle?: string
  ) => {
    setDisplayParent({
      value: parentId,
      label: parentTitle
    });

    if (parentId === -1 && prevParentId && prevParentTitle) {
      props.onRelationshipDeselect(prevParentId, prevParentTitle);
    } else {
      props.onRelationshipSelect(parentId, parentTitle);
    }
  };

  // Does the display list already contain the selected child?
  const listContainsChild = (childId: number) => {
    if (childItemsList) {
      for (let i = 0; i < childItemsList.length; i++) {
        if (childItemsList[i].value === childId) {
          return true;
        }
      }
    }
    return false;
  };

  // Add selected child to display list
  const onSelectChild = (childId: number, childTitle: string) => {
    if (childId === -1) {
      return;
    }
    setDisplayChild({
      value: childId,
      label: childTitle
    });
    if (!listContainsChild(childId) && childItemsList) {
      childItemsList.push({ value: childId, label: childTitle });
    }
    setChildItemsList(childItemsList);
    props.onRelationshipSelect(childId, childTitle);
  };

  // Remove child from display list
  const onDeleteChild = (newList?: RelationshipDisplayFeedback[]) => {
    if (newList) {
      setChildItemsList(newList);
    }
  };

  const onCancel = () => {
    props.toggleModal();
    setParentError(defaultError);
    setChildError(defaultError);
    setGeneralError(defaultError);
    setDisplayChild({ value: -1, label: "Select" });
  };

  return (
    <Modal style={{ width: "402px" }} isOpen={props.isOpen} onToggle={props.toggleModal}>
      <ModalHeader toggle={props.toggleModal}>Edit Relationship</ModalHeader>
      <ModalBody>
        <div style={{ width: "100%", textAlign: "center" }}>
          <div style={{ width: "354px", display: "inline-block" }}>
            <div style={{ paddingBottom: "32px" }}>
              <div style={{ textAlign: "left" }}>
                Edit relationship on this feedback to organize the work and create a hierarchy.
              </div>
              <div style={{ marginTop: "16px", textAlign: "left" }}>
                Parent Feedback
                <Icon
                  className={styles.icon}
                  appName="GeneralLayout"
                  assetName="GENERAL_L_Info"
                  title="Choosing a parent feedback will automatically make this feedback a child item."
                />
              </div>
              <div>
                <ParentFeedbackDropdown
                  options={props.options}
                  value={displayParent}
                  onSelect={onSelectParent}
                />
                {parentError.hasError && <div className={styles.error}>{parentError.message}</div>}
              </div>
            </div>
            <div
              style={{
                paddingTop: "24px",
                marginBottom: "24px",
                borderTop: "1px solid",
                borderColor: "#EBEBEB"
              }}
            >
              <div style={{ textAlign: "left" }}>
                Children Feedback
                <Icon
                  className={styles.icon}
                  appName="GeneralLayout"
                  assetName="GENERAL_L_Info"
                  title="Adding child items will automatically make you a parent item."
                />
              </div>
              <div>
                <ParentFeedbackDropdown
                  options={props.options}
                  value={displayChild}
                  onSelect={onSelectChild}
                />
                {childError.hasError && <div className={styles.error}>{childError.message}</div>}
              </div>
            </div>
            <ChildFeedbackDisplayList
              childItemsList={childItemsList}
              onDeleteChild={onDeleteChild}
              onRelationshipDeselect={props.onRelationshipDeselect}
            />
            {generalError.hasError && <div className={styles.error}>{generalError.message}</div>}
          </div>
        </div>
      </ModalBody>
      <ModalFooter style={{ textAlign: "left" }}>
        <Button onClick={onSave}>Save</Button>
        <Button onClick={onCancel}>Cancel</Button>
      </ModalFooter>
    </Modal>
  );
};
